import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  ArrowLeft,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  History,
  House,
  Ticket,
} from 'lucide-react';

import {
  useNavigate,
} from 'react-router-dom';

import RouteConfig from '../../../routes/RouteConfig';

import {
  memberService,
} from '../../member/services/memberService';

import type {
  MemberInfo,
} from '../../member/types/member.types';

import {
  storeService,
} from '../../store/services/storeService';

import {
  seedService,
} from '../services/seedService';

import type {
  StoreUserPoint,
} from '../types/seed.types';

import type {
  HistoryItem,
  HistoryResponse,
} from '../../history/types/history.types';


type HistoryTab =
  | 'seed'
  | 'store';


interface HistoryUser
  extends MemberInfo {
  pointList: StoreUserPoint[];
}


const PAGE_SIZE = 10;


/**
 * 숫자 포맷
 */
const formatNumber = (
  value:
    | number
    | null
    | undefined,
): string => {
  return new Intl.NumberFormat(
    'ko-KR',
  ).format(
    value ?? 0,
  );
};


/**
 * 날짜 / 시간 포맷
 */
const formatDateTime = (
  value: string,
): string => {
  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime(),
    )
  ) {
    return value;
  }

  return new Intl.DateTimeFormat(
    'ko-KR',
    {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    },
  ).format(date);
};


/**
 * YYYY-MM-DD
 */
const formatLocalDate = (
  date: Date,
): string => {
  const year =
    date.getFullYear();

  const month =
    String(
      date.getMonth() + 1,
    ).padStart(
      2,
      '0',
    );

  const day =
    String(
      date.getDate(),
    ).padStart(
      2,
      '0',
    );

  return `${year}-${month}-${day}`;
};


/**
 * 기본 조회 기간
 *
 * 오늘 기준 한 달 전 ~ 오늘
 */
const getDefaultDateRange =
  (): {
    startDate: string;
    endDate: string;
  } => {
    const end =
      new Date();

    const start =
      new Date();

    start.setMonth(
      start.getMonth() - 1,
    );

    return {
      startDate:
        formatLocalDate(
          start,
        ),

      endDate:
        formatLocalDate(
          end,
        ),
    };
  };


/**
 * 빈 히스토리
 */
const EMPTY_HISTORY:
  HistoryResponse = {
    histories: [],
    totalCount: 0,
  };


export default function SeedHistoryPage() {
  const navigate =
    useNavigate();


  const defaultDateRange =
    useMemo(
      () =>
        getDefaultDateRange(),
      [],
    );


  /**
   * =========================================================
   * 회원 + 매장 포인트
   * =========================================================
   */
  const [
    user,
    setUser,
  ] =
    useState<HistoryUser | null>(
      null,
    );


  /**
   * 사용자 정보 로딩
   */
  const [
    isUserLoading,
    setIsUserLoading,
  ] =
    useState(true);


  /**
   * seed / store
   */
  const [
    activeTab,
    setActiveTab,
  ] =
    useState<HistoryTab>(
      'seed',
    );


  /**
   * 선택 매장
   */
  const [
    selectedStoreId,
    setSelectedStoreId,
  ] =
    useState<number | null>(
      null,
    );


  /**
   * 조회 기간
   */
  const [
    startDate,
    setStartDate,
  ] =
    useState(
      defaultDateRange.startDate,
    );


  const [
    endDate,
    setEndDate,
  ] =
    useState(
      defaultDateRange.endDate,
    );


  /**
   * API page는 0부터 시작
   */
  const [
    currentPage,
    setCurrentPage,
  ] =
    useState(0);


  /**
   * 히스토리
   */
  const [
    historyData,
    setHistoryData,
  ] =
    useState<HistoryResponse>(
      EMPTY_HISTORY,
    );


  const [
    isHistoryLoading,
    setIsHistoryLoading,
  ] =
    useState(false);


  const [
    userErrorMessage,
    setUserErrorMessage,
  ] =
    useState('');


  const [
    historyErrorMessage,
    setHistoryErrorMessage,
  ] =
    useState('');


  /**
   * =========================================================
   * 회원 기본정보 + 매장별 포인트 조회
   *
   * GET /api/v2/users/me
   * GET /api/v2/users/me/store-points
   * GET /api/v2/stores
   * =========================================================
   */
  const loadUser =
    useCallback(
      async (): Promise<void> => {
        try {
          setIsUserLoading(
            true,
          );

          setUserErrorMessage(
            '',
          );


          /**
           * 로그인 사용자 정보
           */
          const member =
            await memberService
              .getMyInfo();


          /**
           * 내 매장별 포인트 +
           * 전체 매장 목록
           */
          const [
            pointListResponse,
            storeListResponse,
          ] =
            await Promise.all([
              seedService
                .getMyStorePointList(),

              storeService
                .getStores(),
            ]);


          const pointList =
            Array.isArray(
              pointListResponse,
            )
              ? pointListResponse
              : [];


          const storeList =
            Array.isArray(
              storeListResponse,
            )
              ? storeListResponse
              : [];


          /**
           * storeId -> storeName
           */
          const storeMap =
            new Map<
              number,
              string
            >(
              storeList.map(
                (store) => [
                  store.storeId,
                  store.storeName,
                ],
              ),
            );


          /**
           * storeId = 0은
           * 라포이트 공용 시드용이므로
           * 매장이용권 목록에서는 제외
           */
          const mappedPointList:
            StoreUserPoint[] =
            pointList
              .filter(
                (point) =>
                  point.storeId !==
                  0,
              )
              .map(
                (point) => ({
                  ...point,

                  storeName:
                    storeMap.get(
                      point.storeId,
                    ) ??
                    point.storeName ??
                    '',
                }),
              );


          const userData:
            HistoryUser = {
              ...member,

              pointList:
                mappedPointList,
            };


          setUser(
            userData,
          );


          /**
           * 소속 매장을 우선 선택
           */
          const defaultStoreId =
            mappedPointList.find(
              (item) =>
                item.storeId ===
                member.storeId,
            )?.storeId ??
            mappedPointList[0]
              ?.storeId ??
            null;


          setSelectedStoreId(
            defaultStoreId,
          );

        } catch (
          error: unknown
        ) {
          console.error(
            '사용자 정보 조회 실패:',
            error,
          );


          setUser(
            null,
          );

          setSelectedStoreId(
            null,
          );

          setUserErrorMessage(
            '회원 정보를 불러오지 못했습니다.',
          );

        } finally {
          setIsUserLoading(
            false,
          );
        }
      },
      [],
    );


  /**
   * =========================================================
   * 이용 내역 조회
   *
   * GET /api/v2/history/me
   *
   * 시드:
   * storeId = 0
   *
   * 매장이용권:
   * storeId = 선택한 실제 매장
   *
   * 응답:
   *
   * {
   *   totalCount,
   *   histories
   * }
   * =========================================================
   */
  const loadHistory =
    useCallback(
      async (): Promise<void> => {
        if (
          !user ||
          isUserLoading
        ) {
          return;
        }


        /**
         * 매장이용권 탭인데
         * 선택 가능한 매장이 없는 경우
         */
        if (
          activeTab ===
            'store' &&
          selectedStoreId ===
            null
        ) {
          setHistoryData(
            EMPTY_HISTORY,
          );

          setHistoryErrorMessage(
            '',
          );

          return;
        }


        try {
          setIsHistoryLoading(
            true,
          );

          setHistoryErrorMessage(
            '',
          );


          const storeId =
            activeTab ===
            'seed'
              ? 0
              : selectedStoreId;


          const response =
            await seedService
              .getMyHistory({
                storeId:
                  storeId ??
                  undefined,

                startDate,

                endDate,

                page:
                  currentPage,

                size:
                  PAGE_SIZE,
              });


          /**
           * 백엔드 UserHistoryListResponse
           *
           * {
           *   totalCount,
           *   histories
           * }
           */
          setHistoryData({
            totalCount:
              response?.totalCount ??
              0,

            histories:
              Array.isArray(
                response?.histories,
              )
                ? response.histories
                : [],
          });

        } catch (
          error: unknown
        ) {
          console.error(
            '이용 내역 조회 실패:',
            error,
          );


          setHistoryData(
            EMPTY_HISTORY,
          );


          setHistoryErrorMessage(
            '이용 내역을 불러오지 못했습니다.',
          );

        } finally {
          setIsHistoryLoading(
            false,
          );
        }
      },
      [
        activeTab,
        currentPage,
        endDate,
        isUserLoading,
        selectedStoreId,
        startDate,
        user,
      ],
    );


  /**
   * 최초 사용자 조회
   */
  useEffect(
    () => {
      void loadUser();
    },
    [
      loadUser,
    ],
  );


  /**
   * 사용자 정보가 준비된 후
   * 히스토리 조회
   */
  useEffect(
    () => {
      if (
        isUserLoading ||
        !user
      ) {
        return;
      }

      void loadHistory();
    },
    [
      isUserLoading,
      loadHistory,
      user,
    ],
  );


  /**
   * 조회 조건 변경 시
   * 1페이지로 이동
   */
  useEffect(
    () => {
      setCurrentPage(
        0,
      );
    },
    [
      activeTab,
      selectedStoreId,
      startDate,
      endDate,
    ],
  );


  /**
   * 안전한 매장 포인트 목록
   */
  const pointList =
    useMemo(
      () =>
        user?.pointList ??
        [],
      [
        user,
      ],
    );


  /**
   * 안전한 히스토리 목록
   */
  const histories =
    useMemo(
      () =>
        historyData.histories ??
        [],
      [
        historyData.histories,
      ],
    );


  /**
   * 선택된 매장
   */
  const selectedStore =
    useMemo(
      () =>
        pointList.find(
          (item) =>
            item.storeId ===
            selectedStoreId,
        ) ??
        null,
      [
        pointList,
        selectedStoreId,
      ],
    );


  /**
   * 제목
   */
  const historyTitle =
    activeTab ===
    'seed'
      ? '라포이트 시드 내역'
      : `${
          selectedStore
            ?.storeName ??
          '매장'
        } 이용권 내역`;


  /**
   * 총 페이지
   */
  const totalPages =
    Math.ceil(
      (
        historyData.totalCount ??
        0
      ) /
        PAGE_SIZE,
    );


  /**
   * 포인트 증감
   */
  const getHistoryDelta = (
    item: HistoryItem,
  ): number => {
    return (
      item.pointDelta ??
      0
    );
  };


  /**
   * 처리자 표시
   *
   * 현재 USER API에서
   * 처리자 이름을 표시
   */
  const getActorDisplay = (
    item: HistoryItem,
  ): string => {
    if (
      item.actorUserId ===
        null ||
      item.actorUserId ===
        undefined
    ) {
      return '시스템';
    }

    return `${item.actorUserName}`;
  };


  /**
   * 증감량 UI
   */
  const renderHistoryAmount = (
    item: HistoryItem,
  ) => {
    const amount =
      getHistoryDelta(
        item,
      );

    const isPositive =
      amount > 0;

    const isZero =
      amount === 0;


    return (
      <span
        className={[
          'font-bold',

          isZero
            ? 'text-gray-500'
            : isPositive
              ? 'text-emerald-600'
              : 'text-red-600',
        ].join(
          ' ',
        )}
      >
        {isPositive
          ? '+'
          : ''}

        {formatNumber(
          amount,
        )}
      </span>
    );
  };


  /**
   * =========================================================
   * 사용자 정보 Loading
   * =========================================================
   */
  if (
    isUserLoading
  ) {
    return (
      <main className="grid min-h-screen place-items-center bg-gray-50 px-4">

        <div className="text-center">

          <div
            className="mx-auto h-9 w-9 animate-spin rounded-full border-[3px] border-gray-200 border-t-gray-900"
            role="status"
            aria-label="사용자 정보 불러오는 중"
          />


          <p className="mt-3 text-sm text-gray-500">
            회원 정보를 불러오고 있습니다.
          </p>

        </div>

      </main>
    );
  }


  /**
   * =========================================================
   * 사용자 정보 Error
   * =========================================================
   */
  if (!user) {
    return (
      <main className="grid min-h-screen place-items-center bg-gray-50 px-4">

        <section className="w-full max-w-md rounded-3xl border border-gray-200 bg-white p-7 text-center shadow-sm">

          <span className="mx-auto grid h-11 w-11 place-items-center rounded-full bg-red-100 font-black text-red-600">
            !
          </span>


          <h1 className="mt-4 text-xl font-bold text-gray-900">
            정보를 불러올 수 없습니다
          </h1>


          <p className="mt-2 text-sm text-gray-500">
            {userErrorMessage ||
              '회원 정보를 확인할 수 없습니다.'}
          </p>


          <button
            type="button"
            onClick={() =>
              void loadUser()
            }
            className="mt-5 h-11 w-full rounded-xl bg-gray-900 text-sm font-bold text-white"
          >
            다시 시도
          </button>

        </section>

      </main>
    );
  }


  return (
    <main className="min-h-screen bg-gray-50 px-4 py-5 text-gray-900 sm:px-6 lg:px-8">

      <section className="mx-auto w-full max-w-6xl">


        {/* ===============================================
            Header
            =============================================== */}
        <header className="mb-5 flex items-center gap-3">

          <button
            type="button"
            onClick={() =>
              navigate(
                RouteConfig
                  .User
                  .Home
                  .fullPath,
              )
            }
            className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-gray-200 bg-white text-gray-700 shadow-sm transition hover:bg-gray-100"
            aria-label="사용자 홈으로 돌아가기"
          >
            <ArrowLeft
              size={21}
            />
          </button>


          <div className="min-w-0">

            <p className="text-xs font-black tracking-[0.12em] text-amber-700">
              USAGE HISTORY
            </p>


            <h1 className="mt-1 text-2xl font-black tracking-[-0.03em] sm:text-3xl">
              이용 내역
            </h1>

          </div>

        </header>


        {/* ===============================================
            Tab
            =============================================== */}
        <section className="mb-5 grid grid-cols-2 gap-2 rounded-2xl border border-gray-200 bg-white p-2 shadow-sm">

          <button
            type="button"
            onClick={() => {
              setActiveTab(
                'seed',
              );

              setCurrentPage(
                0,
              );
            }}
            className={[
              'flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-bold transition',

              activeTab ===
              'seed'
                ? 'bg-gray-900 text-white'
                : 'text-gray-500 hover:bg-gray-100',
            ].join(
              ' ',
            )}
          >
            <Ticket
              size={18}
            />

            라포이트 시드
          </button>


          <button
            type="button"
            onClick={() => {
              setActiveTab(
                'store',
              );

              setCurrentPage(
                0,
              );
            }}
            className={[
              'flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-bold transition',

              activeTab ===
              'store'
                ? 'bg-amber-500 text-white'
                : 'text-gray-500 hover:bg-gray-100',
            ].join(
              ' ',
            )}
          >
            <House
              size={18}
            />

            매장이용권
          </button>

        </section>


        {/* ===============================================
            Search
            =============================================== */}
        <section className="mb-5 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">

          <div className="flex flex-col gap-3 md:flex-row md:items-end">

            {activeTab ===
              'store' && (

              <label className="block md:w-56 md:shrink-0">

                <span className="mb-1.5 block text-xs font-bold text-gray-500">
                  매장 선택
                </span>


                <select
                  value={
                    selectedStoreId ??
                    ''
                  }
                  disabled={
                    pointList.length ===
                    0
                  }
                  onChange={(
                    event,
                  ) => {
                    const value =
                      event
                        .target
                        .value;

                    setSelectedStoreId(
                      value
                        ? Number(
                            value,
                          )
                        : null,
                    );

                    setCurrentPage(
                      0,
                    );
                  }}
                  className="h-11 w-full rounded-xl border border-gray-300 bg-white px-3 text-sm font-semibold outline-none transition focus:border-amber-500 focus:ring-2 focus:ring-amber-100 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                >

                  {pointList.length ===
                    0 && (

                    <option value="">
                      이용 가능한 매장이 없습니다
                    </option>

                  )}


                  {pointList.map(
                    (item) => (

                      <option
                        key={
                          item.storeId
                        }
                        value={
                          item.storeId
                        }
                      >
                        {
                          item.storeName
                        }
                      </option>

                    ),
                  )}

                </select>

              </label>

            )}


            <div className="min-w-0 flex-1">

              <span className="mb-1.5 block text-xs font-bold text-gray-500">
                조회 기간
              </span>


              <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2">

                <input
                  type="date"
                  value={
                    startDate
                  }
                  max={
                    endDate
                  }
                  onChange={(
                    event,
                  ) => {
                    setStartDate(
                      event
                        .target
                        .value,
                    );

                    setCurrentPage(
                      0,
                    );
                  }}
                  aria-label="조회 시작일"
                  className="h-11 min-w-0 w-full rounded-xl border border-gray-300 px-2 text-xs outline-none transition focus:border-amber-500 focus:ring-2 focus:ring-amber-100 sm:px-3 sm:text-sm"
                />


                <span
                  className="shrink-0 text-sm font-semibold text-gray-400"
                  aria-hidden="true"
                >
                  ~
                </span>


                <input
                  type="date"
                  value={
                    endDate
                  }
                  min={
                    startDate
                  }
                  onChange={(
                    event,
                  ) => {
                    setEndDate(
                      event
                        .target
                        .value,
                    );

                    setCurrentPage(
                      0,
                    );
                  }}
                  aria-label="조회 종료일"
                  className="h-11 min-w-0 w-full rounded-xl border border-gray-300 px-2 text-xs outline-none transition focus:border-amber-500 focus:ring-2 focus:ring-amber-100 sm:px-3 sm:text-sm"
                />

              </div>

            </div>

          </div>

        </section>


        {/* ===============================================
            History
            =============================================== */}
        <section className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm">

          <header className="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-4 sm:px-5">

            <div>

              <p className="text-xs font-bold text-gray-400">
                총{' '}
                {formatNumber(
                  historyData.totalCount,
                )}
                건
              </p>


              <h2 className="mt-1 text-lg font-black">
                {historyTitle}
              </h2>

            </div>


            <History
              size={22}
              className="text-gray-300"
            />

          </header>


          {isHistoryLoading ? (

            <div className="grid min-h-56 place-items-center">

              <div className="text-center">

                <div className="mx-auto h-8 w-8 animate-spin rounded-full border-[3px] border-gray-200 border-t-gray-900" />


                <p className="mt-3 text-sm text-gray-500">
                  이용 내역을 불러오고 있습니다.
                </p>

              </div>

            </div>

          ) : historyErrorMessage ? (

            <div className="grid min-h-56 place-items-center px-5 text-center">

              <div>

                <p className="text-sm font-semibold text-red-600">
                  {
                    historyErrorMessage
                  }
                </p>


                <button
                  type="button"
                  onClick={() =>
                    void loadHistory()
                  }
                  className="mt-4 rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-bold text-white"
                >
                  다시 시도
                </button>

              </div>

            </div>

          ) : histories.length ===
            0 ? (

            <div className="grid min-h-56 place-items-center px-5 text-center">

              <div>

                <CalendarDays
                  size={34}
                  className="mx-auto text-gray-300"
                />


                <p className="mt-3 text-sm font-semibold text-gray-600">
                  조회된 이용 내역이 없습니다.
                </p>


                <p className="mt-1 text-xs text-gray-400">
                  기간 또는 매장을 변경해 확인해 주세요.
                </p>

              </div>

            </div>

          ) : (

            <>

              {/* Mobile */}
              <div className="divide-y divide-gray-100 md:hidden">

                {histories.map(
                  (item) => (

                    <article
                      key={
                        item.historyId
                      }
                      className="px-4 py-4"
                    >

                      <div className="flex items-start justify-between gap-3">

                        <div className="min-w-0">

                          <p className="text-xs font-semibold text-gray-400">
                            일시
                          </p>


                          <p className="mt-1 text-sm font-medium text-gray-700">
                            {formatDateTime(
                              item.createdAt,
                            )}
                          </p>

                        </div>


                        <div className="shrink-0 text-right text-sm">

                          <p className="text-xs font-semibold text-gray-400">
                            변동
                          </p>


                          <div className="mt-1">
                            {renderHistoryAmount(
                              item,
                            )}
                          </div>

                        </div>

                      </div>


                      <div className="mt-3 grid grid-cols-1 gap-3 rounded-xl bg-gray-50 p-3 sm:grid-cols-2">

                        <div className="min-w-0">

                          <p className="text-xs font-semibold text-gray-400">
                            처리자
                          </p>


                          <p className="mt-1 break-words text-sm font-semibold text-gray-700">
                            {getActorDisplay(
                              item,
                            )}
                          </p>

                        </div>


                        <div className="min-w-0">

                          <p className="text-xs font-semibold text-gray-400">
                            메모
                          </p>


                          <p className="mt-1 break-words text-sm leading-5 text-gray-600">
                            {item.memo?.trim() ||
                              '-'}
                          </p>

                        </div>

                      </div>

                    </article>

                  ),
                )}

              </div>


              {/* Desktop */}
              <div className="hidden overflow-x-auto md:block">

                <table className="w-full min-w-[760px]">

                  <thead className="bg-gray-50 text-xs font-bold text-gray-500">

                    <tr>

                      <th className="px-5 py-3 text-left">
                        일시
                      </th>

                      <th className="px-5 py-3 text-left">
                        메모
                      </th>

                      <th className="px-5 py-3 text-left">
                        처리자
                      </th>

                      <th className="px-5 py-3 text-right">
                        변동
                      </th>

                    </tr>

                  </thead>


                  <tbody className="divide-y divide-gray-100">

                    {histories.map(
                      (item) => (

                        <tr
                          key={
                            item.historyId
                          }
                          className="transition hover:bg-gray-50"
                        >

                          <td className="whitespace-nowrap px-5 py-4 text-sm text-gray-500">
                            {formatDateTime(
                              item.createdAt,
                            )}
                          </td>


                          <td className="max-w-[360px] px-5 py-4">

                            <p className="break-words text-sm leading-5 text-gray-700">
                              {item.memo?.trim() ||
                                '-'}
                            </p>

                          </td>


                          <td className="px-5 py-4 text-sm font-semibold text-gray-600">
                            {getActorDisplay(
                              item,
                            )}
                          </td>


                          <td className="whitespace-nowrap px-5 py-4 text-right text-sm">
                            {renderHistoryAmount(
                              item,
                            )}
                          </td>

                        </tr>

                      ),
                    )}

                  </tbody>

                </table>

              </div>

            </>

          )}


          {/* =============================================
              Pagination
              ============================================= */}
          {totalPages >
            1 && (

            <footer className="flex items-center justify-between border-t border-gray-200 px-4 py-3 sm:px-5">

              <button
                type="button"
                onClick={() =>
                  setCurrentPage(
                    (previous) =>
                      Math.max(
                        0,
                        previous -
                          1,
                      ),
                  )
                }
                disabled={
                  currentPage ===
                  0
                }
                className="inline-flex h-9 items-center gap-1 rounded-lg border border-gray-200 px-3 text-xs font-bold text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
              >

                <ChevronLeft
                  size={15}
                />

                이전

              </button>


              <span className="text-xs font-bold text-gray-500">
                {currentPage +
                  1}{' '}
                /{' '}
                {totalPages}
              </span>


              <button
                type="button"
                onClick={() =>
                  setCurrentPage(
                    (previous) =>
                      Math.min(
                        totalPages -
                          1,
                        previous +
                          1,
                      ),
                  )
                }
                disabled={
                  currentPage >=
                  totalPages -
                    1
                }
                className="inline-flex h-9 items-center gap-1 rounded-lg border border-gray-200 px-3 text-xs font-bold text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
              >

                다음

                <ChevronRight
                  size={15}
                />

              </button>

            </footer>

          )}

        </section>

      </section>

    </main>
  );
}